import React, {useEffect, useState} from 'react';
import {
    Alert,
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogContent,
    Divider,
    Link,
    Stack,
    Tab,
    Tabs,
    TextField,
    Typography
} from "@mui/material";
import IconButton from "@mui/material/IconButton";
import CloseIcon from '@mui/icons-material/Close';
import GoogleIcon from '@mui/icons-material/Google';
import FacebookIcon from '@mui/icons-material/Facebook';
import Logo from "../img/clockmaker-logo.png";
import {useLanguage} from "../context/LanguageContext";
import {
    PROVIDERS,
    registerWithEmail,
    resendVerificationEmail,
    resetPassword,
    signInWithEmail,
    signInWithProvider,
} from "../auth/pwaAuth";

import ArrowBackIcon from '@mui/icons-material/ArrowBack';

const MODES = {signIn: 'signIn', register: 'register', reset: 'reset'};

// Пауза перед повторной отправкой письма для сброса пароля, сек (как в виджете Xsolla)
const RESEND_DELAY = 60;

// Палитра повторяет кастомный стиль виджета Xsolla (public/login-popup3.css)
const COLORS = {
    facebook: 'rgb(59, 93, 165)',
    google: '#444',
    submit: 'rgb(255, 128, 21)',
    submitDisabled: 'rgb(255, 185, 132)',
    text: '#1c1c1c',
    muted: '#6b6b6b',
    border: '#cfd4da',
};

const pill = {
    borderRadius: '28px',
    height: 48,
    textTransform: 'uppercase',
    fontWeight: 600,
    fontSize: 13,
    letterSpacing: '0.04em',
    boxShadow: 'none',
};

const fieldSx = {
    '& .MuiOutlinedInput-root': {
        borderRadius: '10px',
        backgroundColor: '#fff',
        '& fieldset': {borderColor: COLORS.border},
        '&:hover fieldset': {borderColor: '#9aa3ad'},
        '&.Mui-focused fieldset': {borderColor: 'rgb(50, 113, 239)', borderWidth: 2},
    },
};

// Иконка успеха как в виджете Xsolla: синий круг 40x40 с галочкой
const SuccessCheckmark = () => (
    <Box
        className="auth-success-checkmark"
        sx={{
            backgroundColor: 'rgb(0, 115, 247)',
            width: 40,
            height: 40,
            margin: '0 6px 12px',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            border: 0,
            borderRadius: '50%',
            boxShadow: 'rgba(0, 115, 247, 0.3) 0px 2px 4px',
            color: '#fff',
        }}
    >
        <Box component="svg" viewBox="0 0 24 24" fill="currentColor" sx={{width: 24, height: 24}}>
            <path fillRule="evenodd" clipRule="evenodd"
                  d="M20.4432 6.53411C20.9766 7.055 20.9868 7.90972 20.4659 8.44316L10.3256 18.8278C10.0563 19.1036 9.68184 19.2511 9.29672 19.2331C8.9116 19.2151 8.55255 19.0334 8.3101 18.7336L4.95037 14.5797C4.48149 14 4.57133 13.15 5.15104 12.6811C5.73074 12.2123 6.58077 12.3021 7.04965 12.8818L9.45458 15.8552L18.5341 6.55684C19.055 6.02339 19.9097 6.01322 20.4432 6.53411Z"/>
        </Box>
    </Box>
);

const methodLabelKey = (method) => {
    if (method === PROVIDERS.google) return 'auth_method_google';
    if (method === PROVIDERS.facebook) return 'auth_method_facebook';
    return 'auth_method_password';
};

// Диалог входа через Firebase Auth: Google, Facebook, email + пароль, сброс пароля.
// После успешного входа вызывается onSignedIn(user); перезагрузку страницы делает вызывающий код.
const LoginDialog = ({open, onClose, onSignedIn, prefillEmail, initialError}) => {
    const {getT, language} = useLanguage();

    const [mode, setMode] = useState(MODES.register);
    const [email, setEmail] = useState(prefillEmail || '');
    const [password, setPassword] = useState('');
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState(null);
    const [errorCode, setErrorCode] = useState(null);
    const [info, setInfo] = useState(null);
    // Письмо для сброса пароля отправлено: вместо формы показываем экран успеха
    const [resetSent, setResetSent] = useState(false);
    // Секунды до разблокировки кнопки повторной отправки
    const [resendLeft, setResendLeft] = useState(0);

    const describeError = (e) => {
        const code = e?.code || '';
        switch (code) {
            case 'auth/invalid-email':
            case 'auth/missing-email':
                return getT('auth_error_invalid_email');
            case 'auth/user-not-found':
                return getT('auth_error_user_not_found');
            case 'auth/invalid-credential':
                // Для Google/Facebook этот код = Firebase не смог проверить ответ провайдера
                // (обычно неверный App Secret или App ID в консоли Firebase), а не пароль
                if (e.viaProvider) return getT('auth_error_provider_invalid');
                return getT('auth_error_wrong_credentials');
            case 'auth/wrong-password':
            case 'auth/invalid-login-credentials':
                return getT('auth_error_wrong_credentials');
            case 'auth/email-already-in-use':
                return getT('auth_error_email_in_use');
            case 'auth/weak-password':
            case 'auth/password-does-not-meet-requirements':
                return getT('auth_error_weak_password');
            case 'auth/too-many-requests':
                return getT('auth_error_too_many');
            case 'auth/network-request-failed':
                return getT('auth_error_network');
            case 'auth/operation-not-allowed':
                return getT('auth_error_not_allowed');
            case 'auth/email-not-verified':
                return getT('auth_error_email_not_verified');
            case 'auth/account-exists-with-different-credential': {
                // Firebase не доверяет email от Facebook и не привязывает его сам к существующему аккаунту.
                // Список способов входа может быть пустым (email enumeration protection) — тогда общий текст.
                if (e.email) setEmail(e.email);
                const method = e.methods && e.methods[0];
                if (!method) return getT('auth_error_account_exists_unknown');
                return getT('auth_error_account_exists').replace('{method}', getT(methodLabelKey(method)));
            }
            default:
                return getT('auth_error_generic');
        }
    };

    useEffect(() => {
        if (open) {
            // Как у виджета Xsolla: по умолчанию открыта вкладка регистрации; известному игроку — вход
            setMode(prefillEmail ? MODES.signIn : MODES.register);
            setPassword('');
            setInfo(null);
            setResetSent(false);
            setResendLeft(0);
            if (prefillEmail) setEmail(prefillEmail);
            setError(initialError ? describeError(initialError) : null);
            setErrorCode(initialError ? initialError.code || null : null);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, prefillEmail, initialError]);

    const counting = resendLeft > 0;
    useEffect(() => {
        if (!counting) return undefined;
        const timer = setInterval(() => setResendLeft((left) => (left > 0 ? left - 1 : 0)), 1000);
        return () => clearInterval(timer);
    }, [counting]);

    const resendVerification = async () => {
        setBusy(true);
        try {
            if (email && password) {
                await resendVerificationEmail(email, password);
                setInfo(getT('auth_verification_sent'));
                setError(null);
            }
        } catch (e) {
            setError(describeError(e));
        } finally {
            setBusy(false);
        }
    };

    const switchMode = (next) => {
        setMode(next);
        setError(null);
        setErrorCode(null);
        setInfo(null);
        setResetSent(false);
        setResendLeft(0);
    };

    const run = async (action) => {
        setBusy(true);
        setError(null);
        setErrorCode(null);
        setInfo(null);
        try {
            const user = await action();
            if (user) onSignedIn(user);
        } catch (e) {
            if (e?.code === 'auth/popup-closed-by-user' || e?.code === 'auth/cancelled-popup-request') {
                return;
            }
            console.log('auth error', e);
            setError(describeError(e));
            setErrorCode(e?.code || null);
        } finally {
            setBusy(false);
        }
    };

    const submitEmailForm = (event) => {
        event.preventDefault();
        if (mode === MODES.reset) {
            run(async () => {
                await resetPassword(email);
                setResetSent(true);
                setResendLeft(RESEND_DELAY);
                return null;
            });
        } else if (mode === MODES.register) {
            run(async () => {
                await registerWithEmail(email, password);
                setInfo(getT('auth_verification_sent') || 'Confirmation letter was sent to your email!');
                return null;
            });
        } else {
            run(async () => {
                return signInWithEmail(email, password);
            });
        }
    };

    const lang = (language || 'en').toLowerCase();
    // В японском и китайском предложение закрывается собственным знаком точки
    const sentenceStop = (lang === 'ja' || lang === 'zh') ? '。' : '.';
    const termsUrl = "https://belka-games.com/" + lang + "/terms-of-service";
    const privacyUrl = "https://belka-games.com/" + lang + "/privacy";
    const canSubmit = email.trim().length > 0 && (mode === MODES.reset || password.length > 0) && resendLeft === 0;

    // Экран успеха после отправки письма: вместо формы иконка и текст с адресом
    const resetSuccess = mode === MODES.reset && resetSent;
    const resetSentText = getT('auth_reset_sent') || '';
    const resetSentParts = resetSentText.split('{email}');
    const resetSentBefore = resetSentParts[0];
    const resetSentAfter = resetSentParts.slice(1).join('{email}');

    const submitLabel = mode === MODES.reset ? getT('auth_reset_send') :
        mode === MODES.register ? getT('auth_create_account') : getT('auth_sign_in');
    // Отсчёт прямо в названии кнопки: «Отправить ссылку (59)»
    const submitText = resendLeft > 0 ? `${submitLabel} (${resendLeft})` : submitLabel;

    return <Dialog
        open={open}
        onClose={busy ? undefined : onClose}
        fullWidth
        maxWidth="xs"
        className="login-dialog"
        PaperProps={{sx: {borderRadius: '12px', color: COLORS.text}}}
    >
        <IconButton
            aria-label="close"
            onClick={onClose}
            disabled={busy}
            sx={{position: 'absolute', right: 8, top: 8, color: (theme) => theme.palette.grey[600]}}
        >
            <CloseIcon/>
        </IconButton>

        <DialogContent sx={{px: {xs: 3, sm: 5}, pt: 4, pb: 4}}>
            {mode === MODES.reset && (
                <Link component="button" type="button" underline="hover"
                      className="auth-back-link"
                      sx={{
                          fontSize: 14,
                          color: 'rgb(50, 113, 239)',
                          mb: 3,
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: 0.5,
                      }}
                      onClick={() => switchMode(MODES.signIn)}>
                    <ArrowBackIcon sx={{ fontSize: 16 }} />
                    {getT('auth_back')}
                </Link>
            )}
            <Box sx={{display: 'flex', justifyContent: 'center', mb: 2}}>
                <Box component="img" src={Logo} alt="Clockmaker" sx={{width: 200, maxWidth: '80%'}}/>
            </Box>

            {mode !== MODES.reset && <Tabs
                value={mode}
                onChange={(_, value) => switchMode(value)}
                variant="fullWidth"
                className="auth-tabs"
                sx={{
                    mb: 3,
                    minHeight: 40,
                    '& .MuiTab-root': {minHeight: 40, fontWeight: 600, fontSize: 13, letterSpacing: '0.04em', color: COLORS.muted},
                    '& .Mui-selected': {color: 'rgb(50, 113, 239)'},
                    '& .MuiTabs-indicator': {backgroundColor: 'rgb(50, 113, 239)'},
                }}
            >
                <Tab className="auth-tab-signin" value={MODES.signIn} label={getT('auth_sign_in')}/>
                <Tab className="auth-tab-register" value={MODES.register} label={getT('auth_sign_up')}/>
            </Tabs>}

            {resetSuccess && <Box sx={{display: 'flex', flexDirection: 'column', alignItems: 'center'}} className="auth-reset-success">
                <SuccessCheckmark/>
                <Box sx={{mb: 2, textAlign: 'center'}} className="auth-reset-success-text">
                    {resetSentBefore}<b>{email}</b>{resetSentAfter}
                </Box>
            </Box>}

            {mode === MODES.reset && !resetSent && <>
                <Typography variant="h6" sx={{mb: 1, textAlign: 'center', fontWeight: 600}}>
                {getT('auth_forgot_title')}
            </Typography>
                <Box sx={{mb:2, textAlign: 'center'}}>{getT('auth_forgot_descr')}</Box>
            </>}

            {mode !== MODES.reset && <Stack spacing={1.5} sx={{mb: 2.5}}>
                <Button
                    className="auth-facebook-btn"
                    variant="contained"
                    disableElevation
                    startIcon={<FacebookIcon/>}
                    disabled={busy}
                    onClick={() => run(() => signInWithProvider(PROVIDERS.facebook))}
                    sx={{...pill, backgroundColor: COLORS.facebook, color: '#fff', '&:hover': {backgroundColor: 'rgb(48, 77, 140)'}}}
                >{getT('auth_facebook')}</Button>
                <Button
                    className="auth-google-btn"
                    variant="outlined"
                    startIcon={<GoogleIcon/>}
                    disabled={busy}
                    onClick={() => run(() => signInWithProvider(PROVIDERS.google))}
                    sx={{...pill, backgroundColor: '#fff', color: COLORS.google, borderColor: COLORS.google, '&:hover': {backgroundColor: '#f3f3f3', borderColor: COLORS.google}}}
                >{getT('auth_google')}</Button>
            </Stack>}

            <Box component="form" onSubmit={submitEmailForm}>
                <Stack spacing={2}>
                    {!resetSuccess && <Box>
                        <Typography component="label" htmlFor="auth-email-input" sx={{display: 'block', fontSize: 14, fontWeight: 500, mb: 0.75}}>
                            {getT('auth_email')}*
                        </Typography>
                        <TextField
                            id="auth-email-input"
                            className="auth-email"
                            type="email"
                            autoComplete="email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            required
                            fullWidth
                            size="medium"
                            sx={fieldSx}
                        />
                    </Box>}
                    {mode !== MODES.reset && <Box>
                        <Typography component="label" htmlFor="auth-password-input" sx={{display: 'block', fontSize: 14, fontWeight: 500, mb: 0.75}}>
                            {getT('auth_password')}*
                        </Typography>
                        <TextField
                            id="auth-password-input"
                            className="auth-password"
                            type="password"
                            autoComplete={mode === MODES.register ? 'new-password' : 'current-password'}
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            required
                            fullWidth
                            size="medium"
                            sx={fieldSx}
                        />
                    </Box>}

                    {mode === MODES.register && <Typography sx={{fontSize: 13, color: COLORS.muted, textAlign: 'center'}}>
                        {getT('auth_agree_prefix')}{' '}
                        <Link href={termsUrl} target="_blank" rel="noopener" underline="hover">{getT('terms')}</Link>
                        {' '}{getT('auth_agree_and')}{' '}
                        <Link href={privacyUrl} target="_blank" rel="noopener" underline="hover">{getT('privacy')}</Link>{sentenceStop}
                    </Typography>}

                    {error && <Alert severity="error" className="auth-error">
                        {error}
                        {/*{errorCode && <Typography component="div" sx={{fontSize: 11, opacity: 0.7, mt: 0.5}} className="auth-error-code">{errorCode}</Typography>}*/}
                        {errorCode === 'auth/email-not-verified' && (
                            <Button className="auth-resend-btn" size="small" onClick={resendVerification} disabled={busy}>
                                {getT('auth_resend_email') || 'Отправить письмо еще раз'}
                            </Button>
                        )}
                    </Alert>}
                    {info && <Alert severity="success" className="auth-info">{info}</Alert>}

                    <Button
                        className="auth-submit"
                        type="submit"
                        variant="contained"
                        disableElevation
                        disabled={busy || !canSubmit}
                        sx={{
                            ...pill,
                            backgroundColor: COLORS.submit,
                            color: '#fff',
                            '&:hover': {backgroundColor: 'rgb(235, 115, 15)'},
                            '&.Mui-disabled': {backgroundColor: COLORS.submitDisabled, color: '#fff'},
                        }}
                    >
                        {busy ? <CircularProgress size={22} color="inherit"/> : submitText}
                    </Button>

                    {mode === MODES.signIn && <>
                        <Link component="button" type="button" underline="hover"
                              className="auth-forgot-link"
                              sx={{fontSize: 14, color: 'rgb(50, 113, 239)'}}
                              onClick={() => switchMode(MODES.reset)}>
                            {getT('auth_forgot')}
                        </Link>
                    </>}
                    {mode === MODES.signIn && <>
                    <Divider />
                    <Typography sx={{fontSize: 13, color: COLORS.muted, textAlign: 'center'}}>
                        {getT('auth_agree_prefix')}{' '}
                        <Link href={termsUrl} target="_blank" rel="noopener" underline="hover">{getT('terms')}</Link>
                        {' '}{getT('auth_agree_and')}{' '}
                        <Link href={privacyUrl} target="_blank" rel="noopener" underline="hover">{getT('privacy')}</Link>.
                    </Typography></>}

                </Stack>
            </Box>
        </DialogContent>
    </Dialog>;
};

export default LoginDialog;
